/**
 * API zarf sözleşmeleri.
 *
 * Tüm `/api/v1` yanıtları bu biçimleri kullanır. Ayrıntılı kurallar ve örnek
 * gövdeler: docs/standards/api-standard.md
 *
 * Hata biçimi RFC 9457 (Problem Details) temellidir; `errors` alanı Laravel'in
 * doğrulama hata yapısıyla uyumlu tutulmuştur.
 */
import type { CorrelationId, UtcDateTime } from './identifiers'

/** Makine tarafından okunabilir hata kodları. HTTP durumundan bağımsızdır. */
export type ApiErrorCode =
  | 'validation_failed'
  | 'unauthenticated'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'idempotency_key_reused'
  | 'invalid_state_transition'
  | 'version_conflict'
  | 'rate_limited'
  | 'tenant_context_missing'
  | 'server_error'
  | 'service_unavailable'
  /*
   * QR ve masa oturumu kodları.
   *
   * Katalog `docs/standards/api-standard.md` içindedir ve gerekçeleri orada
   * duruyor. Burada tekrarlanmaz; ama union **kapalı** tutulur: istemcinin
   * dallandığı bir kodun tipe girmemesi, arayüzde sessizce ele alınmayan bir
   * durum bırakır.
   */
  | 'qr_token_invalid'
  | 'qr_token_revoked'
  | 'qr_table_unavailable'
  | 'qr_tokens_missing'
  | 'visit_challenge_invalid'
  | 'visit_capacity_exceeded'
  | 'visit_session_invalid'
  | 'visit_closed'
  | 'menu_empty'
  | 'menu_not_published'
  /*
   * Sepet ve sipariş kodları (Faz 4).
   *
   * Satır düzeyindeki sorunlar (`product_unavailable` ve devamı) hem sepet
   * yanıtındaki `issues` dizisinde hem de ekleme reddinin hata kodu olarak
   * döner: ikisi de "bu satır neden sipariş edilemiyor" sorusunu cevaplar ve
   * ayrı iki sözlük tutmak, birine eklenen bir kodun diğerinde eksik
   * kalmasına açıktı.
   */
  | 'cart_empty'
  | 'cart_not_orderable'
  | 'product_removed'
  | 'product_unavailable'
  | 'variant_unavailable'
  | 'modifier_unavailable'
  | 'selection_below_minimum'
  | 'selection_above_maximum'
  | 'order_transition_invalid'
  | 'order_version_conflict'
  | 'order_reason_required'
  | 'order_item_already_cancelled'
  /*
   * Telefon teslim kodları (Faz 6).
   *
   * `custody_code_invalid` ve `custody_code_locked` AYRI: ilki "tekrar dene",
   * ikincisi "artık deneme". Tek koda indirmek, misafirin kilitlendiğini
   * anlamadan denemeye devam etmesi demekti.
   */
  | 'custody_terms_missing'
  /*
   * Aynı müşterinin İKİNCİ açık teslimi.
   *
   * Şemadaki kısmi benzersizlik indeksi bunu zaten reddediyordu; kod, o reddi
   * personele anlaşılır bir cümleye çevirmek için eklendi. Öncesinde ekrana
   * ham SQL düşüyordu — tablo adı, sütun listesi ve onay kodunun argon2
   * hash'i dâhil.
   */
  | 'custody_already_open'
  | 'custody_transition_invalid'
  | 'custody_reason_required'
  | 'custody_version_conflict'
  | 'custody_actor_required'
  | 'custody_code_invalid'
  | 'custody_code_locked'
  /*
   * Puan kullanım kodları (Faz 6).
   *
   * `loyalty_redemption_step` ile `loyalty_redemption_cap` AYRI: ilki "başka
   * bir miktar seç", ikincisi "bu kadarını tek seferde kullanamazsın". Tek
   * koda indirmek, personelin miktarı düşürüp düşürmeyeceğini bilememesi
   * demekti.
   *
   * `loyalty_insufficient_balance` yığınlardan geliyor, ledger toplamından
   * değil: süresi dolmuş ama henüz temizlenmemiş puan toplamda görünür,
   * harcanabilir değildir.
   */
  | 'loyalty_redemption_step'
  | 'loyalty_redemption_cap'
  | 'loyalty_redemption_unavailable'
  | 'loyalty_reward_unavailable'
  | 'loyalty_insufficient_balance'
  | 'loyalty_reversal_invalid'
  | 'loyalty_already_reversed'
  /*
   * Müşteri oturumu (Faz 7).
   *
   * `unauthenticated`'ten AYRI olmak zorunda: sıradan bir süre dolması
   * "yeniden giriş yapın" der ve arayüz bunu sessizce halleder. Bu ise
   * kullanılmış bir yenileme token'ının ikinci kez sunulduğu, yani zincirde
   * ikinci bir taraf olduğu anlamına geliyor — kullanıcıya gösterilecek şey
   * bir gecikme değil, bir uyarı.
   */
  | 'customer_session_replayed'
  /*
   * Puan hesabı bağı (Faz 7 dilim 7a-3).
   *
   * Üçü AYRI, çünkü üçünün cevabı farklı bir eylem:
   *
   * - `loyalty_link_code_invalid` — kod geçersiz, süresi dolmuş ya da
   *   kullanılmış. Ayırt edilmiyor ve edilmemeli: "biçim doğru, yalnızca geç
   *   kaldın" bilgisi kod tahmin edene geri bildirim olurdu. Yapılacak şey
   *   personelden yeni kod istemek.
   * - `loyalty_account_already_linked` — hesap başka birine bağlı. Bağı
   *   yalnızca o kişi koparabilir; personelin yapabileceği bir şey yok.
   * - `loyalty_link_conflict` — müşterinin bu restoranda zaten bağlı bir
   *   hesabı var. Çözüm kullanıcının kendi elinde: önce onu koparacak.
   */
  | 'loyalty_link_code_invalid'
  | 'loyalty_account_already_linked'
  | 'loyalty_link_conflict'
  /*
   * Yorum moderasyonu (Faz 7 dilim 7c-1, arayüzü Faz 8 dilim 8h-2).
   *
   * İkisi AYRI, çünkü ikisinin muhatabı farklı: `review_report_open` işletmeye
   * "zaten şikâyet ettin, bekle" der; `review_report_resolved` platform
   * yöneticisine "bu şikâyet karara bağlanmış" der. Tek koda indirmek, iki
   * farklı ekranda aynı metni doğru sanarak göstermek olurdu.
   */
  | 'review_report_open'
  | 'review_report_resolved'
  /*
   * Misafirin YAZDIĞI yorum (Faz 7 dilim 7c-2).
   *
   * Moderasyon kodlarından ayrı duruyorlar, çünkü muhatapları farklı: bu
   * ikisini misafir görüyor, üsttekileri işletme ve platform.
   *
   * İkisi de AYRI, çünkü cevapları farklı: süre dolduysa yapılacak bir şey
   * yok; zaten yazılmışsa yapılacak şey var olanı silip yeniden yazmak
   * (düzenleme ucu yok, gerekçesi {@link ReviewWriter} içinde).
   */
  | 'review_window_closed'
  | 'review_already_written'
  /*
   * E-posta doğrulama (Faz 7 dilim 7c-2).
   *
   * Doğrulama kapısı GİRİŞTE değil: postası gecikmiş bir misafir menüden ve
   * siparişten alıkonulmamalı. Yorum, kimliğin gerçekten karşılığı olan ilk
   * yer ve kapı orada — dolayısıyla bu kod misafirin ekranına düşüyor.
   */
  | 'customer_email_unverified'
  | 'customer_email_already_verified'
  | 'customer_verification_invalid'
  /*
   * Self-servis işletme kaydı.
   *
   * Misafir doğrulamasından farkı, kapının GERÇEKTEN kapalı olması:
   * `owner_email_unverified`, onay masasının doğrulanmamış bir başvuruyu
   * onaylamayı reddettiği durumda dönüyor. Kayıt eden kişiyi platform hiç
   * tanımıyor ve elimizdeki tek kanal o adres.
   *
   * Tanınmayan, süresi geçmiş ve eskitilmiş bağlantı AYNI kodu alıyor:
   * ayırmak, elinde bir bağlantı olan birine onun "bir zamanlar geçerliydi"
   * mi yoksa "hiç olmadı" mı olduğunu söylerdi.
   */
  | 'owner_email_unverified'
  | 'owner_email_already_verified'
  | 'owner_verification_invalid'
  /*
   * Personel parola sıfırlama (Faz C).
   *
   * Süresi dolmuş, kullanılmış ve hiç var olmayan bağlantı AYNI kodu alıyor:
   * ayırmak, bir e-postanın sıfırlama isteği olup olmadığını dışarıdan
   * sorgulanabilir kılardı.
   */
  | 'staff_reset_invalid'
  /*
   * Personelin iki adımlı doğrulaması (C-03).
   *
   * `mfa_code_required` giriş ucundan dönüyor ve parola DOĞRUYKEN dönüyor —
   * yani "bu hesapta MFA açık" bilgisini veriyor. Bu bilinçli: istemcinin kod
   * alanını göstermesi için başka bir yol yok ve bilgi zaten parolayı bilen
   * kişiye açılıyor.
   *
   * `mfa_code_invalid` hem kurulum onayında hem girişte aynı: kodun TOTP mi
   * yoksa kurtarma kodu mu olduğu SÖYLENMİYOR, çünkü ayırmak hangi ikisinin
   * denendiğini dışarıdan sorgulanabilir kılardı.
   */
  | 'mfa_code_required'
  | 'mfa_code_invalid'
  /*
   * Ziyareti hesaba ekleme (Faz 7 dilim 7b-2).
   *
   * İkisi AYRI, çünkü biri kullanıcının kendi geçmişi ("zaten ekledin"),
   * diğeri bir çakışma ("bu oturum başkasının"). Tek koda indirmek, ikinci
   * durumda kullanıcıya kendi hatasıymış gibi bir cümle gösterirdi.
   */
  | 'visit_participation_duplicate'
  | 'visit_participation_claimed'
  /*
   * Görsel yükleme (Faz 3 dilim 3f) — PERSONEL tarafı.
   *
   * Beşi de ayrı, çünkü beşinin cevabı farklı bir düzeltme: dosyayı küçült,
   * daha büyüğünü seç, çözünürlüğü düşür, dosya bozuk, biçim desteklenmiyor.
   * "Görsel yüklenemedi" tek kodu, kullanıcıyı aynı dosyayı tekrar denemeye
   * bırakırdı.
   */
  | 'image_too_large'
  | 'image_too_small'
  | 'image_dimensions_exceeded'
  | 'image_unreadable'
  | 'image_unsupported_format'
  /*
   * Mutfak bileti (Faz 5) ve menü yayını — PERSONEL tarafı.
   */
  | 'kitchen_ticket_transition_invalid'
  | 'kitchen_ticket_version_conflict'
  | 'modifier_group_empty'
  /*
   * Yazıcı ve ajan eşleştirme (Faz 6) — PERSONEL tarafı.
   */
  | 'print_agent_invalid'
  | 'print_pairing_invalid'
  | 'printer_inactive'
  /*
   * POS/KDS terminali (ana plan §3) — PERSONEL tarafı.
   */
  | 'terminal_invalid'
  | 'terminal_pairing_invalid'
  /*
   * Destek talepleri (Faz 9 dilim 9d).
   *
   * İkisi AYRI, çünkü kullanıcının yapacağı şey farklı:
   *
   * - `support_ticket_limit` — açık talep tavanı doldu. Çözüm var olan bir
   *   talebi kapatmak; yeni konu açmak değil.
   * - `support_ticket_duplicate` — aynı konuda zaten açık bir talep var.
   *   Çözüm o talebe yazmak. Tek koda indirmek, kullanıcıyı yanlış eylemi
   *   denemeye yönlendirirdi.
   */
  | 'support_ticket_limit'
  | 'support_ticket_duplicate'
  /*
   * Kupon kodları (Post-MVP PM-1).
   *
   * Sekiz ayrı kod, çünkü sekizinin cevabı farklı bir eylem. Tek bir "kupon
   * geçersiz", misafiri aynı kodu tekrar tekrar denemeye iterdi ve gerçek
   * sebep (asgari tutar tutmadı) ekranda hiçbir yerde yazmazdı.
   *
   * `coupon_not_found` ile `coupon_inactive` AYRI ve bu bilinçli bir
   * sızıntı: kod deneyen biri hangi kodların var olduğunu öğrenebilir. Kabul
   * ediliyor çünkü alternatifi, süresi dolmuş kuponunu deneyen gerçek
   * misafire "böyle bir kupon yok" demek olurdu — ve kupon kodları zaten
   * gizli değil, basılıp dağıtılıyor. Kaba kuvvet savunması hız sınırında.
   *
   * `coupon_requires_account` kimliksiz misafire çıkıyor: müşteri başına
   * sınır taşıyan bir kupon, "başına" ekseni olmayan anonim katılımcıya
   * uygulanamıyor. Sınırı uygulayamayacağımız yerde kuponu reddetmek,
   * sınırı yazıp uygulamamaktan dürüst.
   */
  | 'coupon_not_found'
  | 'coupon_inactive'
  | 'coupon_wrong_branch'
  | 'coupon_minimum_not_met'
  | 'coupon_exhausted'
  | 'coupon_customer_limit_reached'
  | 'coupon_requires_account'
  | 'coupon_empty_cart'
  /*
   * Abonelik paketi (Faz 9 dilim 9e, ADR-019).
   *
   * İkisi AYRI, çünkü kullanıcının yapacağı şey farklı:
   *
   * - `plan_limit_reached` — kaynak tavanı doldu. Çözüm ya var olan bir
   *   kaynağı kapatmak ya da paketi büyütmek; mesaj hangi paketin kaç
   *   taşıdığını söylüyor.
   * - `plan_feature_unavailable` — modül pakete dahil değil. Kaynak silmek
   *   işe yaramaz; tek çözüm paket değişikliği.
   *
   * Yetki reddiyle de karıştırılmamalı: yetkisizlik 404 (kaynağın varlığını
   * sızdırmamak için), paket eksikliği 402 — burada gizlenecek bir şey yok,
   * aksine kullanıcıya ne yapması gerektiği söyleniyor.
   */
  | 'plan_limit_reached'
  | 'plan_feature_unavailable'
  /**
   * Özellik platform genelinde geçici olarak kapatıldı (503).
   *
   * `plan_feature_unavailable` ile karıştırılmamalı: orada kullanıcının
   * yapabileceği bir şey var (paketi yükseltmek), burada yok. Kapatma bir
   * operasyon kararı ve geçici — istemci yeniden denemeli, kullanıcıya
   * "yükseltin" dememeli.
   */
  | 'feature_disabled'
  /** Yalnızca istemci tarafında üretilir; sunucudan gelmez. */
  | 'network_error'

/**
 * Standart hata gövdesi.
 *
 * `message` son kullanıcıya gösterilebilir Türkçe metindir.
 * `code` istemcinin dallanma yapacağı sabittir — metne göre dallanma yasaktır.
 */
export interface ApiError {
  readonly code: ApiErrorCode
  readonly message: string
  /** Alan bazlı doğrulama hataları: `{ "email": ["Geçersiz e-posta."] }` */
  readonly errors?: Readonly<Record<string, readonly string[]>>
  readonly correlationId?: CorrelationId
  /** 429 ve 503 yanıtlarında saniye cinsinden bekleme süresi. */
  readonly retryAfterSeconds?: number
}

/** Başarılı tekil yanıt zarfı. */
export interface ApiResponse<TData> {
  readonly data: TData
  readonly meta?: Readonly<Record<string, unknown>>
}

/**
 * Cursor tabanlı sayfalama.
 *
 * Neden offset değil: Sipariş listesi sürekli değiştiği için `page=2` isteği
 * araya giren yeni kayıtlar yüzünden kayıt atlar veya tekrarlar. Cursor bu
 * sorunu ortadan kaldırır.
 */
export interface PageInfo {
  readonly nextCursor: string | null
  readonly previousCursor: string | null
  readonly hasNextPage: boolean
  readonly perPage: number
}

export interface PaginatedResponse<TItem> {
  readonly data: readonly TItem[]
  readonly pageInfo: PageInfo
  readonly meta?: Readonly<Record<string, unknown>>
}

/**
 * İyimser eşzamanlılık kontrolü taşıyan kaynaklar.
 *
 * İki çalışanın aynı siparişi eşzamanlı güncellemesi bu alanla yakalanır:
 * istemci okuduğu `version`'ı geri gönderir, sunucu uyuşmazsa `version_conflict`
 * döner (bkz. R-07).
 */
export interface Versioned {
  readonly version: number
  readonly updatedAt: UtcDateTime
}

/** Bir yanıt gövdesinin hata zarfı olup olmadığını daraltır. */
export function isApiError(value: unknown): value is ApiError {
  if (typeof value !== 'object' || value === null) {
    return false
  }

  const candidate = value as Record<string, unknown>
  return typeof candidate['code'] === 'string' && typeof candidate['message'] === 'string'
}

/**
 * Hata kodlarının ÇALIŞMA ZAMANI listesi.
 *
 * Union'ın kendisi yukarıda ve gerekçeleriyle birlikte duruyor; bu dizi onun
 * kopyası değil, **denetlenebilir** hâli: bir TypeScript union'ı derlemeden
 * sonra kaybolur ve "her kodun karşılığı var mı" sorusunu soran bir test
 * yazılamaz.
 *
 * İkisinin ayrışması aşağıdaki kapıyla derleme anında yakalanıyor.
 */
export const API_ERROR_CODES = [
  'validation_failed',
  'unauthenticated',
  'forbidden',
  'not_found',
  'conflict',
  'idempotency_key_reused',
  'invalid_state_transition',
  'version_conflict',
  'rate_limited',
  'tenant_context_missing',
  'server_error',
  'service_unavailable',
  'qr_token_invalid',
  'qr_token_revoked',
  'qr_table_unavailable',
  'qr_tokens_missing',
  'visit_challenge_invalid',
  'visit_capacity_exceeded',
  'visit_session_invalid',
  'visit_closed',
  'menu_empty',
  'menu_not_published',
  'cart_empty',
  'cart_not_orderable',
  'product_removed',
  'product_unavailable',
  'variant_unavailable',
  'modifier_unavailable',
  'selection_below_minimum',
  'selection_above_maximum',
  'order_transition_invalid',
  'order_version_conflict',
  'order_reason_required',
  'order_item_already_cancelled',
  'custody_terms_missing',
  'custody_already_open',
  'custody_transition_invalid',
  'custody_reason_required',
  'custody_version_conflict',
  'custody_actor_required',
  'custody_code_invalid',
  'custody_code_locked',
  'loyalty_redemption_step',
  'loyalty_redemption_cap',
  'loyalty_redemption_unavailable',
  'loyalty_reward_unavailable',
  'loyalty_insufficient_balance',
  'loyalty_reversal_invalid',
  'loyalty_already_reversed',
  'customer_session_replayed',
  'loyalty_link_code_invalid',
  'loyalty_account_already_linked',
  'loyalty_link_conflict',
  'review_report_open',
  'review_report_resolved',
  'review_window_closed',
  'review_already_written',
  'customer_email_unverified',
  'customer_email_already_verified',
  'customer_verification_invalid',
  'owner_email_unverified',
  'owner_email_already_verified',
  'owner_verification_invalid',
  'staff_reset_invalid',
  'mfa_code_required',
  'mfa_code_invalid',
  'visit_participation_duplicate',
  'visit_participation_claimed',
  'image_too_large',
  'image_too_small',
  'image_dimensions_exceeded',
  'image_unreadable',
  'image_unsupported_format',
  'kitchen_ticket_transition_invalid',
  'kitchen_ticket_version_conflict',
  'modifier_group_empty',
  'print_agent_invalid',
  'print_pairing_invalid',
  'printer_inactive',
  'terminal_invalid',
  'terminal_pairing_invalid',
  'support_ticket_limit',
  'support_ticket_duplicate',
  'coupon_not_found',
  'coupon_inactive',
  'coupon_wrong_branch',
  'coupon_minimum_not_met',
  'coupon_exhausted',
  'coupon_customer_limit_reached',
  'coupon_requires_account',
  'coupon_empty_cart',
  'plan_limit_reached',
  'plan_feature_unavailable',
  'feature_disabled',
  'network_error',
] as const

/*
 * Sapma kapısı — İKİ YÖNLÜ.
 *
 * Union'a eklenip diziye eklenmeyen bir kod `Missing` tarafını, diziye
 * eklenip union'da olmayan bir kod `Extra` tarafını `never` yapıyor ve atama
 * derlenmiyor. Tek yönlü bir kontrol, listeye fazladan yazılmış bir kodu
 * sessizce geçirirdi.
 *
 * Çalışma zamanında hiçbir şey yapmıyor; işi derleyiciye söylemek.
 */
type MissingErrorCode = Exclude<ApiErrorCode, (typeof API_ERROR_CODES)[number]>
type ExtraErrorCode = Exclude<(typeof API_ERROR_CODES)[number], ApiErrorCode>

const errorCodeDriftGuard: [MissingErrorCode, ExtraErrorCode][] = []

void errorCodeDriftGuard
